import {
  ConsumerServicesOrderAddressData,
  ConsumerServicesOrderData,
  ConsumerServicesOrderIdentificationData,
  ConsumerServicesOrderSubjectData
} from '../_data/reports/consumer/consumer-services-order-data';
import {Component, OnDestroy, OnInit, ViewChild, inject} from '@angular/core';
import {I18nService} from '../_services/gui/i18n.service';
import {TabBarService} from '../_services/gui/tab-bar.service';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {MatStepper, MatStep, MatStepLabel} from '@angular/material/stepper';
import {AuthenticationService} from '../_services/authentication.service';
import {collectErrors, setOptional, setRequired} from '../_utils/forms';
import {
  ConsumerRequest,
  ConsumerRequestConsumer,
  ConsumerRequestCreditCard,
  ConsumerRequestEmployment,
  ConsumerRequestMetaInfo,
  ConsumerRequestPhone
} from '../_data/reports/consumer/consumer-request';
import {Address} from '../_data/reports/address';
import {ReportsServicesBaseComponent} from '../common/reports-services-base.component';
import {NotificationService} from '../_services/gui/notification.service';
import {BranchesService} from '../_services/data/branches.service';
import {Branch} from '../_data/admin/branch';
import {Dirs, EamlRequest, Ifs, IfsRequest, newIfs2Address} from '../_data/reports/ifs/ifs-request';
import {Subscription} from 'rxjs';
import {newHawk} from '../_utils/requests';
import {MemberInfo} from '../_data/admin/member-info';
import {tap} from 'rxjs/operators';
import {stringify} from 'flatted';
import {i18nKeyMap, maskStart, nullIfBlank, reformatDate, reformatSin} from '../_utils/lang';
import {getIfsProductName, isMds} from '../_utils/products';
import {IfsProductType, IfsProductTypeMap, ProductType} from '../_constants/types';
import {CONSUMER_REQUEST_CACHE_KEY, EAML_REQUEST_CACHE_KEY, IFS_PREFIX, IFS_REQUEST_CACHE_KEY} from '../_constants/constants';
import {ConfirmModalService} from '../_services/gui/confirm-modal.service';
import {idxApplicationChannels, idxInteractionTypes, idxProductTypes, 
  ConsumerSubjectFormComponent} from './subject-form/standard-subject-form/consumer-subject-form/consumer-subject-form.component';
import {SidenavComponent} from '../sidenav/sidenav.component';
import {ServicesSideCardsComponent} from '../common/services-side-cards/services-side-cards.component';
import {NgClass, NgTemplateOutlet, AsyncPipe, KeyValuePipe} from '@angular/common';
import {ValidationErrorsComponent} from '../common/forms/validation-errors/validation-errors.component';
import {AmlSubjectFormComponent} from './subject-form/standard-subject-form/aml-subject-form/aml-subject-form.component';
import {EamlSubjectFormComponent} from './subject-form/standard-subject-form/eaml-subject-form/eaml-subject-form.component';
import {AvsSubjectFormComponent} from './subject-form/standard-subject-form/avs-subject-form/avs-subject-form.component';
import {BvsSubjectFormComponent} from './subject-form/standard-subject-form/bvs-subject-form/bvs-subject-form.component';
import {DirsSubjectFormComponent} from './subject-form/standard-subject-form/dirs-subject-form/dirs-subject-form.component';
import {EdirsSubjectFormComponent} from './subject-form/standard-subject-form/edirs-subject-form/edirs-subject-form.component';
import {DirsLookupFormComponent} from './subject-form/dirs-lookup-form/dirs-lookup-form.component';
import {HawkFormComponent} from './subject-form/hawk-form/hawk-form.component';
import {TranslateModule} from '@ngx-translate/core';

@Component({
    selector: 'app-consumer-services',
    templateUrl: './consumer-services.component.html',
    styleUrls: ['./consumer-services.component.scss'],
    imports: [SidenavComponent, ServicesSideCardsComponent, MatStepper, MatStep, MatStepLabel, ReactiveFormsModule, 
      NgClass, ValidationErrorsComponent, ConsumerSubjectFormComponent, AmlSubjectFormComponent, EamlSubjectFormComponent,
      AvsSubjectFormComponent, BvsSubjectFormComponent, DirsSubjectFormComponent, EdirsSubjectFormComponent, 
      DirsLookupFormComponent, HawkFormComponent, NgTemplateOutlet, AsyncPipe, KeyValuePipe, TranslateModule]
})
export class ConsumerServicesComponent extends ReportsServicesBaseComponent implements OnInit, OnDestroy {

  public  readonly i18nService = inject(I18nService);
  reformatDate = reformatDate;
  reformatSin = reformatSin;
  maskStart = maskStart;

  /**
   * Used heavily by the html template to know which form fields to display.
   * Form fields for different products are housed in components that conditionally display by this field's value
   */
  currentProductType: ProductType = 'consumer';

  /** Used by the consumer subject form component to know if it needs to display the IDX input form fields */
  isIdxRequest: boolean = false;

  inquiryTypes: {individual: string; joint: string};
  ifsProducts: IfsProductTypeMap;

  orderInformationForm: UntypedFormGroup;
  orderInformationFormSubmitted: boolean = false;
  primarySubjectFormGroup: UntypedFormGroup;
  secondarySubjectFormGroup: UntypedFormGroup;
  isIfsRequest: boolean = false;
  hawkTypes: Map<string, string>;

  @ViewChild('stepper') private stepper: MatStepper;
  @ViewChild('step1') private step1: MatStep;
  @ViewChild('step2') private step2: MatStep;
  @ViewChild('step3') private step3: MatStep;

  private ifsProductsSubscription: Subscription;
  private memberInfo: MemberInfo;


  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly router = inject(Router);
  private readonly tabBarService = inject(TabBarService);
  private readonly modalService = inject(ConfirmModalService);


  private static newConsumerRequestConsumer(cf: ConsumerServicesOrderSubjectData, identifier: 0 | 1): ConsumerRequestConsumer {

    const creditCards = [cf.identification?.card1, cf.identification?.card2]
      .filter(c => !!c?.num)
      .map(c => ({cardType: c.type, cardNumber: c.num} as ConsumerRequestCreditCard));

    const consumer = {
      creditCards:       creditCards,
      dob:               cf.identification?.dob,
      firstName:         cf.firstName,
      lastName:          cf.lastName,
      middleName:        cf.middleName,
      phones:            [{phoneNumber: cf.phone} as ConsumerRequestPhone],
      sin:               cf.identification?.sin,
      addresss:          [cf.currentAddress, cf.prevAddress].filter(a => !!a?.street).map(
        a => ({apt: a.apartmentNum, street: a.street, postal: a.postalCode, province: a.province, city: a.city, civic: a.streetNum} as Address)
      ),
      employments:       [{employer: cf.employer} as ConsumerRequestEmployment],
      subjectIdentifier: identifier,
      suffix:            ''
    } as ConsumerRequestConsumer;

    // If the consumer request is IDX, there will be an IDX object that we can convert to the ixIdent json field
    if(cf.idx) {
      consumer.ixIdent = {
        clientId: cf.idx.clientId,
        applicationChannel: cf.idx.applicationChannel,
        interactionType: cf.idx.interactionType,
        emailAddress: cf.idx.emailAddress,
        ipAddress: cf.idx.ipAddress,
        productType: cf.idx.productType
      };
    }

    return consumer;
  }

  private createConsumerRequest(v, branch: Branch): ConsumerRequest {
    const metaInfo: ConsumerRequestMetaInfo = {
      branchName: v.branchName, lang: this.i18nService.currentLangShortCode,
      memberCode: branch && branch.memberCode, product: branch && branch.product, userReference: v.referenceId
    };

    const consumers: ConsumerRequestConsumer[] = [ConsumerServicesComponent.newConsumerRequestConsumer(this.primarySubjectData, 0)];
    if(this.isJointType()) {
      consumers.push(ConsumerServicesComponent.newConsumerRequestConsumer(this.secondarySubjectData, 1));
    }

    return {
      metaInfo: metaInfo,
      consumers: consumers
    };
  }

  /** When the user changes the branch for the order and chooses a branch that's set up with a normal consumer product code (eg: 7000) */
  private onConsumerRequest(branch: Branch) {
    console.info('Consumer branch chosen');
    this.isIfsRequest = false;
    setOptional(this.orderInformationForm.controls.ifsProduct);
    setRequired(this.orderInformationForm.controls.inquiryType);
    if(isMds(branch.product)) {
      this.currentProductType = 'MDS';
    } else {
      this.currentProductType = 'consumer';
    }

    // Disabling isIdxBranch check to disable IDX product
    // this.branchesService.isIdxBranch(branch.memberCode).subscribe(v => {
    //   this.isIdxRequest = v;
    //   console.debug('Is IDX branch?', v);
    // });
  }

  /** When the user changes the branch for the order and chooses a branch that's set up for IFS */
  private onIfsRequest(memberCode: string) {
    console.info('IFS branch chosen');

    this.isIfsRequest = true;
    setOptional(this.orderInformationForm.controls.inquiryType);
    setRequired(this.orderInformationForm.controls.ifsProduct);

    //Disable product dropdown until after it's loaded.
    this.orderInformationForm.controls.ifsProduct.disable();

    //This gets unsubscribed earlier.  When the user picks any branch.
    this.ifsProductsSubscription = this.i18nService.repeatOnLangChange(() => this.branchesService.getMemberCodeInfo(memberCode))
      .pipe(tap(memberInfo => console.debug('MemberInfo', memberInfo)))
      .subscribe((memberInfo: MemberInfo) => {
        console.debug(memberInfo);
        this.memberInfo = memberInfo;
        this.ifsProducts = Object.fromEntries(
          //For each assigned product that starts with IFS, create an Object entry that's the [key, productName]
          memberInfo.si.productInfos
            .filter(pi => pi.productName.startsWith(IFS_PREFIX))
            .map(pi => {
              const entry: [IfsProductType, string] = [getIfsProductName(pi.product), pi.productName.replace(IFS_PREFIX, '')];
              if(!entry[0]) {
                console.warn(`${pi.product} is not mapped to an ifs product`);
              } else {
                console.debug(`Mapped ${JSON.stringify(pi)} to ${JSON.stringify(entry)}`);
              }
              return entry;
            })
            .filter(entry => !!entry[0])
        ) as IfsProductTypeMap;
        console.info(`${memberCode} is authorized for the following IFS products: ${JSON.stringify(this.ifsProducts)}`);
        //Re-enable the dropdown now that any loading is completed
        this.orderInformationForm.controls.ifsProduct.enable();
      });
  }

  private createIfsRequest(v, branch: Branch): IfsRequest {

    console.debug(stringify(v));

    const ifsRequest: IfsRequest = {
      ifs: ({
        branchName:    branch.branchName,
        lang:          this.i18nService.currentLangShortCode,
        member:        branch && branch.memberCode,
        userReference: v.referenceId
      }) as Ifs
    };
    const ifsProduct: IfsProductType = v.ifsProduct;

    if(ifsProduct === 'hawk') {
      this.setHawkRequest(ifsRequest);
    } else {

      const sd: ConsumerServicesOrderSubjectData = this.primarySubjectData;
      const a: ConsumerServicesOrderAddressData = sd.currentAddress;
      const i: ConsumerServicesOrderIdentificationData = sd.identification;

      ifsRequest.ifs2 = {
        name:       `${sd.firstName ?? ''} ${sd.lastName ?? ''}`,
        dob:        i?.dob,
        creditCard: i?.cc,
        sin:        i?.sin,
        phone:      sd.phone,
        addresses:  a && [newIfs2Address(null, a.apartmentNum, a.streetNum, a.street, a.city, a.province?.toUpperCase(), a.postalCode, null)]
      };

      if(ifsProduct === 'avs') {
        this.setAvsRequest(ifsRequest, a, i, sd);
      } else if(ifsProduct === 'bvs') {
        this.setBvsRequest(ifsRequest, sd, i, a);
      } else if(ifsProduct === 'dirs' || ifsProduct === 'eDirs') {
        this.setDirsRequest(a, sd, ifsProduct, ifsRequest);
      } else if(ifsProduct === 'dirsLookup') {
        this.setDirsLookupRequest(ifsRequest, sd);
      } else if(ifsProduct === 'eAml') {
        this.setEamlRequest(ifsRequest, a, sd, i);
      }
    }

    console.debug(`Generated request for ifs report: ${JSON.stringify(ifsRequest)}`);
    return ifsRequest;

  }

  private setEamlRequest(ifsRequest: IfsRequest, a: ConsumerServicesOrderAddressData, sd: ConsumerServicesOrderSubjectData, i: ConsumerServicesOrderIdentificationData) {
    ifsRequest.hawk = [{city: a.city, phone: sd.phone, postal: a.postalCode, province: a.province, street: a.street, type: 'CURR'}];
    if(i.sin || i.dob) {
      ifsRequest.hawkSubjects = [nullIfBlank({sin: i.sin, dob: i.dob})];
    }
  }

  private setDirsLookupRequest(ifsRequest: IfsRequest, sd: ConsumerServicesOrderSubjectData) {
    ifsRequest.dirsLookup = nullIfBlank({
      phone: sd.phone
    });
  }

  private setDirsRequest(a: ConsumerServicesOrderAddressData, sd: ConsumerServicesOrderSubjectData, ifsProduct: 'dirs' | 'eDirs', ifsRequest: IfsRequest) {
    const d: Dirs = nullIfBlank({
      apt:        a.apartmentNum,
      avsFlag:    '',
      city:       a.city,
      civic:      a.streetNum,
      dirs2Flag:  'Y',
      firstName:  sd.firstName,
      lastName:   sd.lastName,
      middleName: sd.middleName,
      postal:     a.postalCode,
      province:   a.province?.toUpperCase(),
      street:     a.street
    });
    if(ifsProduct === 'dirs') {
      ifsRequest.dirs = d;
    } else {
      ifsRequest.dirsEnhanced = Object.assign(d, {phone: sd.phone});
    }
  }

  private setBvsRequest(ifsRequest: IfsRequest, sd: ConsumerServicesOrderSubjectData, i: ConsumerServicesOrderIdentificationData, a: ConsumerServicesOrderAddressData) {
    ifsRequest.bvs = nullIfBlank({
      firstName:  sd.firstName,
      lastName:   sd.lastName,
      middleName: sd.middleName,
      dob:        i.dob,
      street:     `${a.streetNum} ${a.street}`,
      city:       a.city,
      postal:     a.postalCode,
      phone:      sd.phone,
      prov:       a.province?.toUpperCase()
    });
    if(i.sin) {
      ifsRequest.bvss = {
        sin: i.sin
      };
    }
  }

  private setAvsRequest(ifsRequest: IfsRequest, a: ConsumerServicesOrderAddressData, i: ConsumerServicesOrderIdentificationData, sd: ConsumerServicesOrderSubjectData) {
    ifsRequest.avs = nullIfBlank({
      city:       a.city,
      creditCard: i.cc,
      firstName:  sd.firstName,
      middleName: sd.middleName,
      lastName:   sd.lastName,
      phone:      sd.phone,
      dob:        i.dob,
      postal:     a.postalCode,
      prov:       a.province?.toUpperCase(),
      street:     a.street
    });
    if(i.sin) {
      ifsRequest.avss = {
        sin: i.sin
      };
    }
  }

  private setHawkRequest(ifsRequest: IfsRequest) {
    const sd: any = this.primarySubjectData;

    ifsRequest.ifs2 = {
      sin:       sd.sin1,
      sin2:      sd.sin2,
      dob:       sd.dob1,
      dob2:      sd.dob2,
      addresses: [sd.addr1, sd.addr2, sd.addr3].map(a => newIfs2Address(a.type, a.apartmentNum, a.streetNum, a.street, a.city, a.province?.toUpperCase(), a.postalCode, a.phone))
    };

    ifsRequest.hawk = [sd.addr1, sd.addr2, sd.addr3].map(newHawk).filter(h => h.city || h.phone || h.postal || h.province || h.street || h.type);
    ifsRequest.hawkSubjects = [[sd.sin1, sd.dob1], [sd.sin2, sd.dob2]].map(sinDob => ({sin: sinDob[0], dob: sinDob[1]})).filter(h => h.sin || h.dob);
  }

  private submitConsumerRequest(consumerRequest: ConsumerRequest) {
    sessionStorage.setItem(CONSUMER_REQUEST_CACHE_KEY, JSON.stringify(consumerRequest));
    this.router.navigateByUrl('/consumer-services/credit-report');
  }

  private submitIfsRequest(ifsRequest: IfsRequest) {
    sessionStorage.setItem(IFS_REQUEST_CACHE_KEY, JSON.stringify(ifsRequest));
    this.router.navigateByUrl('/consumer-services/ifs-report');
  }

  private submitEamlRequest(eamlRequest: EamlRequest) {
    sessionStorage.setItem(EAML_REQUEST_CACHE_KEY, JSON.stringify(eamlRequest));
    this.router.navigateByUrl('/consumer-services/enhanced-aml-report');
  }

  private submit() {
    if(this.orderInformationForm.valid) {
      const v = this.orderInformationForm.getRawValue(); //use raw value because reference field might be auto generated and disabled
      const ifsProduct: IfsProductType = v.ifsProduct;
      const branch = {...this.branches.find(b => b.branchName === v.branchName)}; //shallow clone so we don't risk permanently changing the product at the source down below
      // console.debug(`Using branch ${JSON.stringify(branch)}`);

      //AML and Enhanced AML are found in the IFS products list, but their requests aren't handled exactly the same.
      //AML is handled like a regular consumer request, and Enhanced AML is both an HRFA and a regular consumer request mashed together
      if(this.isIfsRequest && (ifsProduct === 'aml' || ifsProduct === 'eAml' || ifsProduct === 'ebvsid')) {
        //Don't run the normal IFS request logic
        this.isIfsRequest = false;
        console.debug(`Looking for a product info with product name "${IFS_PREFIX}${this.ifsProducts[v.ifsProduct]}"`);
        //IFS Branch will have "IFS" as the product, so we'll grab the product code for the chosen IFS product and set that as the Branch's product
        //We only need to do this for AML and EAML requests because other IFS requests don't need to supply the product code.
        branch.product = this.memberInfo?.si?.productInfos?.find(pi => pi.productName === IFS_PREFIX + this.ifsProducts[ifsProduct])?.product ?? '09980';
        console.debug(`IFS product set to ${branch.product}`);
      }

      //This will be creating the appropriate request and forwarding it off the the appropriate report component.
      if(this.isIfsRequest) {
        //Normal IFS Requests
        const ifsRequest = this.createIfsRequest(v, branch);
        this.submitIfsRequest(ifsRequest);
      } else if(ifsProduct === 'eAml') {
        //Enhanced AML Requests
        console.debug('Creating enhanced aml request from hrfa/consumer requests');
        const ifsRequest = this.createIfsRequest(v, branch);
        const consumerRequest = this.createConsumerRequest(v, branch);
        const eamlRequest: EamlRequest = {ifsInquiry: ifsRequest, consumer: consumerRequest};
        this.submitEamlRequest(eamlRequest);
      } else {
        //Normal consumer, IDX, AML or EBVSID requests.
         const consumerRequest = this.createConsumerRequest(v, branch);
         this.submitConsumerRequest(consumerRequest);
      }
    }
  }

  ngOnInit() {
    super.ngOnInit();

    
    this.i18nService
      .getTranslationMap(
        i18nKeyMap('content.keyed.ifs.hawk.types.', ['CURR', 'PREV', 'WORK', 'SHIP', 'BILL'])
      )
      .subscribe(v => this.hawkTypes = v)


    this.i18nService.setTitle('title.consumer-services');
    this.tabBarService.show();

    this.i18nService.getTranslations('content.individual', 'content.joint')
      .subscribe(translations => {
        this.inquiryTypes = {individual: translations[0], joint: translations[1]};
      });

    this.authService.decodedAccessToken$.subscribe(jwt => this.companyName = jwt?.user?.company);

    this.orderInformationForm = this.formBuilder.group({
      branchName:  ['', Validators.required],
      ifsProduct:  '',
      inquiryType: ['', Validators.required],
      referenceId: ['', Validators.compose([Validators.required, Validators.maxLength(24)])]
    });

    // Branches that have automatic reference enabled will need their reference generated on select
    this.autoRef(this.orderInformationForm.controls.branchName, () => this.branches, this.orderInformationForm.get('referenceId'));

    // Trigger different logic, to display different form configurations, based on the chosen branch
    this.selectedBranch(this.orderInformationForm.controls.branchName, () => this.branches).subscribe((branch: Branch) => {
      this.ifsProductsSubscription?.unsubscribe();
      // IFS Branches are identified by their product being "IFS"
      if(branch?.product?.toUpperCase() === 'IFS') {
        this.onIfsRequest(branch.memberCode);
      } else if(branch?.product) { // Otherwise, as long as the product field is truthy, it's a consumer branch
        this.onConsumerRequest(branch);
      }

      // Ensure that step2/3 are set to not completed
      // This will catch an edge case when a user has gone all the way to review for a consumer request
      //  and then gone back and changed to a different type of consumer request (eg: idx)
      if(this.step2) {this.step2.completed = false;}
      if(this.step3) {this.step3.completed = false;}
    });

    this.orderInformationForm.controls.ifsProduct.valueChanges.subscribe((ifsProduct: ProductType) => {
      console.debug(`IFS Product type changed to ${ifsProduct}`);
      this.currentProductType = ifsProduct;
    });

  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.tabBarService.hide();
    this.ifsProductsSubscription?.unsubscribe();
  }

  submitOrderInformation() {
    this.orderInformationFormSubmitted = true;
    if(this.orderInformationForm.valid) {
      this.step1.completed = true;
      this.stepper.next();
    } else {
      console.debug(`Validation errors: ${JSON.stringify(collectErrors(this.orderInformationForm))}`);
    }
  }

  submitPrimarySubjectInfo(f: UntypedFormGroup) {
    //Validity is asserted in the subject form component
    console.debug(`Done with primary subject info!`);
    this.primarySubjectFormGroup = f;
    this.step2.completed = true;
    this.stepper.next();
  }

  isJointType(): boolean {
    return !this.isIfsRequest && this.f.controls.inquiryType.value === 'joint';
  }

  submitSecondarySubjectInfo(f: UntypedFormGroup) {
    //Validity is asserted in the subject form component
    console.debug(`Done with secondary subject info!`);
    this.secondarySubjectFormGroup = f;
    this.step3.completed = true;
    this.stepper.next();
  }

  confirmSubmit(ariaLabelledBy: string, container: HTMLElement) {
    console.debug('Submit review');
    this.modalService.open(
      {i18nKey: 'content.confirm'},
      {i18nKey: 'content.request-report-disclaimer'},
      ariaLabelledBy,
      container
    ).result$.subscribe(accepted => {
      if(accepted) {
        console.debug(`OK clicked.  Submitting request...`);
        this.submit();
      }
    });
  }

  cancel() {
    this.orderInformationFormSubmitted = false;
    this.step1.completed = false;
    this.orderInformationForm.reset();
  }

  back() {
    this.stepper.previous();
  }

  get f(): UntypedFormGroup {
    return this.orderInformationForm;
  }

  get c(): {[p: string]: AbstractControl} {
    return this.orderInformationForm.controls;
  }

  get v(): ConsumerServicesOrderData {
    return this.orderInformationForm.getRawValue();
  }

  get primarySubjectData(): any {
    return !!this.primarySubjectFormGroup ? this.primarySubjectFormGroup.value : null;
  }

  get secondarySubjectData(): ConsumerServicesOrderSubjectData {
    return !!this.secondarySubjectFormGroup ? this.secondarySubjectFormGroup.value : null;
  }

  public get idxProductTypes() {
    return idxProductTypes;
  }

  public get idxInteractionTypes() {
    return idxInteractionTypes;
  }

  public get idxApplicationChannels() {
    return idxApplicationChannels;
  }

}
